/**
 * StageOverrideModal Component
 * Allows staff users to manually override a booking's lifecycle stage
 * Includes reason field for audit trail
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/admin/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/admin/ui/select'
import { Button } from '@/components/admin/ui/button'
import { Textarea } from '@/components/admin/ui/textarea'
import { Label } from '@/components/admin/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { updateBookingStage, LIFECYCLE_STAGES, STAGE_LABELS, type LifecycleStage } from '@/lib/booking-lifecycle'
import { Loader2 } from 'lucide-react'

interface StageOverrideModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bookingId: string
  currentStage: LifecycleStage
  onSuccess?: () => void
}

export function StageOverrideModal({
  open,
  onOpenChange,
  bookingId,
  currentStage,
  onSuccess
}: StageOverrideModalProps) {
  const [selectedStage, setSelectedStage] = useState<LifecycleStage>(currentStage)
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()
  const { user } = useAdminAuth()

  const handleSubmit = async () => {
    if (!user?.id) {
      toast({
        title: 'Error',
        description: 'You must be logged in to update stages',
        variant: 'destructive',
      })
      return
    }

    if (selectedStage === currentStage) {
      toast({
        title: 'No Change',
        description: 'Please select a different stage',
        variant: 'destructive',
      })
      return
    }

    if (!reason.trim()) {
      toast({
        title: 'Reason Required',
        description: 'Please provide a reason for this stage change',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)

    try {
      const result = await updateBookingStage(
        bookingId,
        selectedStage,
        user.id,
        reason.trim()
      )

      if (result.success) {
        toast({
          title: 'Stage Updated',
          description: `Booking moved to ${STAGE_LABELS[selectedStage]}`,
        })
        onSuccess?.()
        setReason('')
      } else {
        throw new Error(result.error || 'Failed to update stage')
      }
    } catch (error) {
      console.error('Error updating stage:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update stage',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Update Booking Stage</DialogTitle>
          <DialogDescription>
            Manually override the current lifecycle stage. This will be logged in the stage history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Stage Display */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Current Stage</Label>
            <div className="p-3 bg-muted rounded-md text-sm font-medium">
              {STAGE_LABELS[currentStage]}
            </div>
          </div>

          {/* New Stage Selection */}
          <div className="space-y-2">
            <Label htmlFor="stage-select">New Stage *</Label>
            <Select
              value={selectedStage}
              onValueChange={(value) => setSelectedStage(value as LifecycleStage)}
            >
              <SelectTrigger id="stage-select">
                <SelectValue placeholder="Select new stage" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LIFECYCLE_STAGES).map(([key, value]) => (
                  <SelectItem
                    key={value}
                    value={value}
                    disabled={value === currentStage}
                  >
                    {STAGE_LABELS[value]}
                    {value === currentStage && ' (Current)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason Field */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Override *</Label>
            <Textarea
              id="reason"
              placeholder="Explain why this stage change is needed..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              This will be recorded in the stage history for audit purposes
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedStage === currentStage || !reason.trim()}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Stage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
